// src/pages/ReadjudicacionMasiva/LoteGestionAbogados.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Box,
    Paper,
    IconButton,
    Tabs,
    Tab,
    Grid,
    Chip,
    Button,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Gavel as GavelIcon,
    PersonAdd as PersonAddIcon,
    RemoveCircle as PersonOffIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import useReadjudicacion from '../../hooks/useReadjudicacion';
import { usePermisos } from '../../utils/usePermisos';
import DesapoderamientoPanel from '../../components/ReadjudicacionMasiva/Abogados/DesapoderamientoPanel';
import AsignacionPanel from '../../components/ReadjudicacionMasiva/Abogados/AsignacionPanel';

const styles = {
    root: {
        backgroundColor: '#f6f8fc',
        minHeight: '100vh',
        paddingTop: '24px',
        paddingBottom: '24px'
    },
    header: {
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
    },
    title: {
        fontWeight: 700,
        color: '#5c6bc0',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flex: 1
    },
    loteInfo: {
        padding: '16px',
        marginBottom: '24px',
        backgroundColor: '#fafafa'
    },
    tabs: {
        marginBottom: '24px'
    },
    tabPanel: {
        paddingTop: '16px'
    },
    loadingBox: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 400
    },
    estadoChip: {
        marginLeft: '8px'
    },
    actionsBar: {
        marginTop: '24px',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '16px'
    }
};

function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
            {...other}
        >
            {value === index && <Box>{children}</Box>}
        </div>
    );
}

const LoteGestionAbogados = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { tienePermiso } = usePermisos();

    const {
        loading,
        lote,
        registros,
        obtenerLote,
        listarRegistros,
        obtenerAbogadosLote,
        obtenerJuzgadosLote,
        obtenerCaracteres,
        obtenerRegistrosDesapoderar,
        desapoderarAbogados,
        finalizarDesapoderamiento,
        asignarAbogados,
        finalizarAsignacion
    } = useReadjudicacion();

    const [tabActual, setTabActual] = useState(0);
    const [abogados, setAbogados] = useState([]);
    const [juzgados, setJuzgados] = useState([]);
    const [caracteres, setCaracteres] = useState([]);
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        tipo: '', // 'desapoderar' o 'asignar'
        callback: null
    });

    // Cambiar tab según estado del lote
    useEffect(() => {
        if (lote) {
            // Estado 1 = Auditado → tab desapoderar (0)
            // Estado 2 = Desapoderado → tab asignar (1)
            if (lote.Estado === 2) {
                setTabActual(1);
            } else if (lote.Estado === 1) {
                setTabActual(0);
            }
        }
    }, [lote]);

    // Verificar permisos
    useEffect(() => {
        if (!tienePermiso('READJUDICACION MASIVA', 'UPDATE')) {
            enqueueSnackbar('No tiene permisos para gestionar abogados', { variant: 'error' });
            navigate('/gestion-lotes?tab=readjudicacion');
        }
    }, [tienePermiso, navigate, enqueueSnackbar]);

    // Cargar datos del lote
    useEffect(() => {
        if (id) {
            cargarDatos();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const cargarDatos = async () => {
        await Promise.all([
            obtenerLote(id),
            listarRegistros(id)
        ]);
        // Cargar abogados, juzgados y caracteres
        const [abogadosData, juzgadosData, caracteresData] = await Promise.all([
            obtenerAbogadosLote(id),
            obtenerJuzgadosLote(id),
            obtenerCaracteres()
        ]);
        setAbogados(abogadosData || []);
        setJuzgados(juzgadosData || []);
        setCaracteres(caracteresData || []);
    };

    const handleTabChange = (event, newValue) => {
        setTabActual(newValue);
    };

    const handleDesapoderar = async (matricula, registrosIds) => {
        const success = await desapoderarAbogados(id, matricula, registrosIds);
        if (success) {
            // Recargar datos
            await cargarDatos();
        }
    };

    const handleAsignar = async (idAbogado, registrosIds, idCaracter) => {
        const success = await asignarAbogados(id, registrosIds, idAbogado, idCaracter);
        if (success) {
            // Recargar datos
            await cargarDatos();
        }
    };

    const handleFinalizarDesapoderamiento = () => {
        setConfirmDialog({
            open: true,
            tipo: 'desapoderar',
            callback: async () => {
                const success = await finalizarDesapoderamiento(id);
                if (success) {
                    setTabActual(1); // Cambiar a pestaña de asignación
                    await cargarDatos();
                }
                setConfirmDialog({ open: false, tipo: '', callback: null });
            }
        });
    };

    const handleFinalizarAsignacion = () => {
        const sinAsignar = registrosFiltrados.filter(r => !r.AbogadoAsignado);
        if (sinAsignar.length > 0) {
            enqueueSnackbar(`No se puede finalizar: ${sinAsignar.length} causa(s) sin abogado asignado.`, { variant: 'warning' });
            return;
        }
        setConfirmDialog({
            open: true,
            tipo: 'asignar',
            callback: async () => {
                const success = await finalizarAsignacion(id);
                if (success) {
                    navigate(`/readjudicacion-masiva/pasaje/${id}`);
                }
                setConfirmDialog({ open: false, tipo: '', callback: null });
            }
        });
    };

    const handleVolver = () => {
        navigate('/gestion-lotes?tab=readjudicacion');
    };

    const getEstadoChip = () => {
        if (!lote) return null;

        const estados = {
            0: { label: 'Pendiente Auditoría', color: 'warning' },
            1: { label: 'Auditado', color: 'primary' },
            2: { label: 'Desapoderado', color: 'secondary' },
            3: { label: 'Asignado', color: 'info' },
            4: { label: 'Listo para Pasaje', color: 'warning' },
            5: { label: 'Finalizado', color: 'success' }
        };

        const estado = estados[lote.Estado] || { label: 'Desconocido', color: 'warning' };
        return <Chip label={estado.label} color={estado.color} size="small" sx={styles.estadoChip} />;
    };

    // Filtrar registros válidos (Ok = 1 o 4)
    const registrosFiltrados = registros.filter(r => r.Ok === 1 || r.Ok === 4);

    if (loading && !lote) {
        return (
            <Container maxWidth={false} sx={styles.root}>
                <Box sx={styles.loadingBox}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (!lote) {
        return (
            <Container maxWidth={false} sx={styles.root}>
                <Typography variant="h6" color="error">
                    Lote no encontrado
                </Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth={false} sx={styles.root}>
            {/* Header */}
            <Box sx={styles.header}>
                <IconButton onClick={handleVolver} size="small" style={{ color: '#5c6bc0' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" style={{ fontWeight: 700, color: '#5c6bc0', display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                    <GavelIcon fontSize="large" />
                    Gestión de Abogados - Lote #{lote.Id}
                </Typography>
                {getEstadoChip()}
            </Box>

            {/* Información del lote */}
            <Paper sx={styles.loteInfo}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                            Expediente Administrativo
                        </Typography>
                        <Typography variant="body1" style={{ fontWeight: 600 }}>
                            {lote.Expediente || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                            Módulo
                        </Typography>
                        <Typography variant="body1" style={{ fontWeight: 600 }}>
                            {lote.Modulo || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                            Total Registros
                        </Typography>
                        <Typography variant="body1" style={{ fontWeight: 600 }}>
                            {registrosFiltrados.length || 0}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                            Abogados en el Lote
                        </Typography>
                        <Typography variant="body1" style={{ fontWeight: 600 }}>
                            {abogados.length || 0}
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>

            {/* Tabs */}
            <Paper sx={styles.tabs}>
                <Tabs
                    value={tabActual}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth"
                >
                    <Tab
                        label="Desapoderar"
                        icon={<PersonOffIcon />}
                        id="tab-0"
                        aria-controls="tabpanel-0"
                        disabled={lote.Estado > 2}
                    />
                    <Tab
                        label="Asignar Abogados"
                        icon={<PersonAddIcon />}
                        id="tab-1"
                        aria-controls="tabpanel-1"
                        disabled={lote.Estado < 2}
                    />
                </Tabs>
            </Paper>

            {/* Tab Panels */}
            <TabPanel value={tabActual} index={0} style={styles.tabPanel}>
                <DesapoderamientoPanel
                    registros={registrosFiltrados}
                    abogados={abogados}
                    juzgados={juzgados}
                    caracteres={caracteres}
                    onDesapoderar={handleDesapoderar}
                    onFiltrar={(filtros) => obtenerRegistrosDesapoderar(id, filtros)}
                    loading={loading}
                />

                {lote.Estado === 1 && (
                    <Box sx={styles.actionsBar}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<CheckCircleIcon />}
                            onClick={handleFinalizarDesapoderamiento}
                            disabled={loading}
                        >
                            Finalizar Desapoderamiento
                        </Button>
                    </Box>
                )}
            </TabPanel>

            <TabPanel value={tabActual} index={1} style={styles.tabPanel}>
                <AsignacionPanel
                    registros={registrosFiltrados}
                    caracteres={caracteres}
                    onAsignar={handleAsignar}
                    loading={loading}
                />

                {lote.Estado === 2 && (
                    <Box sx={styles.actionsBar}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<CheckCircleIcon />}
                            onClick={handleFinalizarAsignacion}
                            disabled={loading}
                        >
                            Finalizar Asignación
                        </Button>
                    </Box>
                )}
            </TabPanel>

            {/* Dialog de confirmación */}
            <Dialog
                open={confirmDialog.open}
                onClose={() => setConfirmDialog({ open: false, tipo: '', callback: null })}
            >
                <DialogTitle>Confirmar Acción</DialogTitle>
                <DialogContent>
                    <Typography>
                        {confirmDialog.tipo === 'desapoderar'
                            ? '¿Está seguro que desea finalizar el desapoderamiento? Esta acción eliminará los abogados marcados como reemplazados y permitirá continuar con la asignación de nuevos abogados.'
                            : '¿Está seguro que desea finalizar la asignación? El lote quedará listo para el pasaje de causas.'}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog({ open: false, tipo: '', callback: null })}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={confirmDialog.callback}
                        color="primary"
                        variant="contained"
                        disabled={loading}
                    >
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default LoteGestionAbogados;
